import React, { useEffect, useMemo, useState } from "react";
import {
    buildBansuriMajorPlan,
    buildBansuriMinorPlan,
    rankFlutesForMajorKey,
    rankFlutesForMinorKey,
    type ScalePlan
} from "./bansuri";
import { VerticalFluteSlider } from "./VerticalFluteSlider";
import { formatSwara } from "./swaraUtils";
import { Music2, SlidersHorizontal, ListMusic, Heart, Gauge, Activity, ArrowRight } from "lucide-react";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const SONG_KEY_OPTIONS = NOTES.flatMap((note) => ([
    { value: `major|${note}`, label: `${note} Major` },
    { value: `minor_natural|${note}`, label: `${note} Minor` }
]));
const SWARA_DEGREE_REFERENCE = ["sa", "re", "ga", "ma", "pa", "dha", "ni", "sa⁺"];
const STRIPE_DONATE_LINK = "https://donate.stripe.com/cNieV68i975Db8Ze4a3Ru00";

function getDifficultyConfig(difficulty: string) {
    switch (difficulty) {
        case "Very Easy":
        case "Easy":
            return { tone: "text-emerald-800", bg: "bg-emerald-50", border: "border-emerald-200" };
        case "Medium":
            return { tone: "text-sky-800", bg: "bg-sky-50", border: "border-sky-200" };
        case "Hard":
            return { tone: "text-amber-800", bg: "bg-amber-50", border: "border-amber-200" };
        case "Very Hard":
            return { tone: "text-rose-800", bg: "bg-rose-50", border: "border-rose-200" };
        default:
            return { tone: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" };
    }
}

function DifficultyBadge({ level }: { level: string }) {
    const config = getDifficultyConfig(level);
    return (
        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${config.bg} ${config.border} ${config.tone}`}>
            {level}
        </span>
    );
}

export function App() {
    const [appMode, setAppMode] = useState<"flute_explorer" | "song_key_assistant">("flute_explorer");
    const [flute, setFlute] = useState("C");
    const [songKeySelection, setSongKeySelection] = useState("minor_natural|C");
    const [baseSa, setBaseSa] = useState<string>("Sa");
    const [difficultyFilter, setDifficultyFilter] = useState<string>("Easy");

    const [songScaleMode, songKey] = songKeySelection.split("|") as ["major" | "minor_natural", string];
    const activeScaleMode = appMode === "song_key_assistant" ? songScaleMode : "major";

    const buildPlanForMode = (fluteBase: string, key: string): ScalePlan => {
        return activeScaleMode === "major"
            ? buildBansuriMajorPlan(fluteBase, key)
            : buildBansuriMinorPlan(fluteBase, key);
    };

    const playable = useMemo(
        () => appMode === "flute_explorer" ? NOTES.map((n) => buildPlanForMode(flute, n)) : [],
        [appMode, flute, activeScaleMode]
    );

    const rankedSongKeyFlutes = useMemo(() => {
        if (appMode !== "song_key_assistant") return [];
        return songScaleMode === "major" ? rankFlutesForMajorKey(songKey) : rankFlutesForMinorKey(songKey);
    }, [appMode, songScaleMode, songKey]);

    const activeScaleInfo = useMemo(() => {
        if (appMode === "flute_explorer") {
            return (playable.find((r) => r.swaras[0] === baseSa) || playable[0]) ?? null;
        }
        return (
            rankedSongKeyFlutes.find((r) => r.newSa === baseSa)
            || rankedSongKeyFlutes.find((r) => r.fluteBase === flute)
            || rankedSongKeyFlutes[0]
        ) ?? null;
    }, [appMode, playable, baseSa, rankedSongKeyFlutes, flute]);

    const difficultyBySa = useMemo(() => {
        const map: Record<string, string> = {};
        if (appMode === "flute_explorer") {
            for (const p of playable) {
                const sa = p.swaras[0];
                if (sa) map[sa] = p.difficulty;
            }
            return map;
        }
        for (const row of rankedSongKeyFlutes) {
            if (row.newSa) map[row.newSa] = row.difficulty;
        }
        return map;
    }, [appMode, playable, rankedSongKeyFlutes]);

    const filteredPlayable = useMemo(() => {
        return playable.filter((r) => {
            if (difficultyFilter === "All") return true;
            if (difficultyFilter === "Easy") return r.difficulty === "Very Easy" || r.difficulty === "Easy";
            if (difficultyFilter === "Hard") return r.difficulty === "Hard" || r.difficulty === "Very Hard";
            return r.difficulty === difficultyFilter;
        });
    }, [playable, difficultyFilter]);

    useEffect(() => {
        if (appMode !== "song_key_assistant") return;
        if (!rankedSongKeyFlutes[0]) return;
        setFlute(rankedSongKeyFlutes[0].fluteBase);
        setBaseSa(rankedSongKeyFlutes[0].newSa);
    }, [appMode, rankedSongKeyFlutes]);

    if (!activeScaleInfo) return null;
    const playabilityHintConfig = getDifficultyConfig(activeScaleInfo.difficulty);
    const scaleMappingConfig = getDifficultyConfig(activeScaleInfo.difficulty);

    const scaleHeaderText = appMode === "song_key_assistant"
        ? activeScaleInfo.targetScale.replace("Natural Minor", "minor").replace("Major", "major")
        : activeScaleInfo.targetScale;
    const playabilityHint = appMode === "flute_explorer"
        ? (
            <>
                With your <strong>{flute} flute</strong> and <strong>Sa</strong> set to <strong>{baseSa}</strong>, you can play songs in <strong>{activeScaleInfo.targetScale}</strong>.
            </>
        )
        : (
            <>
                For {songKey} {songScaleMode === "major" ? "Major" : "Minor"} songs, using <strong>{flute} flute</strong> with Sa at <strong>{baseSa}</strong> is a <strong>{activeScaleInfo.difficulty.toLowerCase()}</strong> setup.
            </>
        );

    return (
        <>
            <div className="bg-mesh" />

            <header className="sticky top-0 z-50 border-b border-slate-300/80 bg-slate-50/95 backdrop-blur-sm">
                <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-3 py-2.5 sm:px-6">
                    <div className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-2.5 py-1.5">
                        <Music2 size={16} className="text-emerald-700" />
                        <span className="text-xs font-semibold tracking-wide text-slate-900 sm:text-sm">bansuri.app</span>
                    </div>
                    <DifficultyBadge level={activeScaleInfo.difficulty} />
                </div>
            </header>

            <main className="px-3 pb-8 pt-4 sm:px-6">
                <h1 className="sr-only">Bansuri Scale Chart and Fingering Explorer</h1>
                <div className="mx-auto max-w-6xl space-y-4">
                    <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(340px,430px)_1fr] lg:items-start">
                        <section className="tool-panel rounded-xl p-3 sm:p-4 lg:sticky lg:top-20">
                            <div className="tool-header -mx-3 -mt-3 mb-3 flex items-center justify-between rounded-t-xl px-3 py-2.5 sm:-mx-4 sm:-mt-4 sm:px-4">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">Live Console</p>
                                    <p className="text-sm font-semibold text-white sm:text-base">{scaleHeaderText}</p>
                                </div>
                            </div>

                            <div className="h-[620px] sm:h-[640px]">
                                <VerticalFluteSlider
                                    fluteScale={flute}
                                    activeSwara={baseSa}
                                    difficulty={activeScaleInfo.difficulty}
                                    difficultyBySa={difficultyBySa}
                                    noteOptions={NOTES}
                                    selectedNote={flute}
                                    onSelectNote={(nextFlute) => {
                                        setFlute(nextFlute);
                                        if (appMode === "flute_explorer") {
                                            setBaseSa("Sa");
                                            setDifficultyFilter("Easy");
                                        } else {
                                            const matchingRow = rankedSongKeyFlutes.find((r) => r.fluteBase === nextFlute);
                                            if (matchingRow) setBaseSa(matchingRow.newSa);
                                        }
                                    }}
                                    onChange={setBaseSa}
                                />
                            </div>

                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                                <div className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5">Flute: <span className="font-semibold text-slate-800">{flute}</span></div>
                                <div className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5">Sa: <span className="font-semibold text-slate-800">{baseSa}</span></div>
                            </div>
                            <div className={`mt-2 rounded-md border px-2.5 py-2 ${playabilityHintConfig.border} ${playabilityHintConfig.bg}`}>
                                <p className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${playabilityHintConfig.tone}`}>Live Playability Hint</p>
                                <p className={`mt-1 text-xs sm:text-sm ${playabilityHintConfig.tone}`}>{playabilityHint}</p>
                            </div>
                        </section>

                        <section className="tool-panel rounded-xl p-3 sm:p-4">
                            <div className="mb-3 flex items-center gap-2">
                                <Activity size={14} className="text-emerald-700" />
                                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">Controls & Buttons</p>
                            </div>

                            <div className="mb-3 inline-flex w-full items-center rounded-lg border border-slate-300 bg-white p-1 text-[11px] sm:text-sm">
                                <button
                                    onClick={() => {
                                        setAppMode("flute_explorer");
                                        setDifficultyFilter("Easy");
                                        setBaseSa("Sa");
                                    }}
                                    className={`w-1/2 rounded-md px-2 py-1.5 font-semibold ${appMode === "flute_explorer" ? "bg-slate-900 text-white" : "text-slate-600"}`}
                                >
                                    Explorer
                                </button>
                                <button
                                    onClick={() => {
                                        setAppMode("song_key_assistant");
                                        setDifficultyFilter("Easy");
                                    }}
                                    className={`w-1/2 rounded-md px-2 py-1.5 font-semibold ${appMode === "song_key_assistant" ? "bg-slate-900 text-white" : "text-slate-600"}`}
                                >
                                    Assistant
                                </button>
                            </div>

                            <div className="mb-3 flex items-center gap-2">
                                <SlidersHorizontal size={15} className="text-slate-600" />
                                <h2 className="text-sm font-semibold text-slate-800">
                                    Playable Scales for {flute} Flute
                                </h2>
                            </div>

                            {appMode === "flute_explorer" && (
                                <>
                                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Difficulty</p>
                                    <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
                                        {["All", "Easy", "Medium", "Hard"].map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => setDifficultyFilter(level)}
                                                className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${difficultyFilter === level
                                                    ? "border-slate-900 bg-slate-900 text-white"
                                                    : "border-slate-300 bg-white text-slate-700"
                                                    }`}
                                            >
                                                {level}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="mt-3 space-y-1.5 max-h-[220px] overflow-y-auto pr-0.5">
                                        {filteredPlayable.map((r) => {
                                            const conf = getDifficultyConfig(r.difficulty);
                                            const active = baseSa === r.swaras[0];
                                            return (
                                                <button
                                                    key={r.targetScale}
                                                    onClick={() => setBaseSa(r.swaras[0]!)}
                                                    className={`w-full rounded-md border p-2 text-left ${active ? "border-slate-800 bg-slate-800 text-white" : `${conf.border} ${conf.bg} text-slate-700`}`}
                                                >
                                                    <p className="text-xs font-semibold sm:text-sm">{r.targetScale}</p>
                                                    <p className={`mt-0.5 text-[11px] font-mono ${active ? "text-slate-200" : "text-slate-500"}`}>Sa -> {r.swaras[0]}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {appMode === "song_key_assistant" && (
                                <>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Song Key</label>
                                        <select
                                            value={songKeySelection}
                                            onChange={(e) => {
                                                setSongKeySelection(e.target.value);
                                                setDifficultyFilter("Easy");
                                            }}
                                            className="text-xs sm:text-sm"
                                        >
                                            {SONG_KEY_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mt-3 space-y-1.5 max-h-[360px] overflow-y-auto pr-0.5">
                                        {rankedSongKeyFlutes.map((row, idx) => {
                                            const conf = getDifficultyConfig(row.difficulty);
                                            const active = flute === row.fluteBase;
                                            return (
                                                <button
                                                    key={`${row.fluteBase}-${row.targetScale}`}
                                                    onClick={() => {
                                                        setFlute(row.fluteBase);
                                                        setBaseSa(row.newSa);
                                                    }}
                                                    className={`w-full rounded-md border p-2 text-left ${active ? "border-slate-900 bg-slate-900 text-white" : `${conf.border} ${conf.bg} text-slate-700`}`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold sm:text-sm">{row.fluteBase} flute</span>
                                                        <span className={`text-[10px] font-semibold ${active ? "text-slate-200" : "text-slate-500"}`}>#{idx + 1}</span>
                                                    </div>
                                                    <p className={`mt-0.5 text-[11px] ${active ? "text-slate-200" : "text-slate-500"}`}>Sa: {row.newSa}</p>
                                                    <p className={`mt-0.5 text-[11px] ${active ? "text-slate-200" : "text-slate-500"}`}>{row.difficulty}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </section>
                    </div>

                    <section className="tool-panel rounded-xl p-4">
                        <div className="mb-2 flex items-center gap-2">
                            <ListMusic size={16} className="text-slate-600" />
                            <h2 className="text-sm font-semibold text-slate-800 sm:text-base">Scale Mapping</h2>
                        </div>
                        <div className="space-y-2 sm:hidden">
                            {activeScaleInfo.westernNotes.map((note, i) => (
                                <div key={`mobile-map-${i}`} className="grid grid-cols-[44px_16px_1fr_58px] items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-2">
                                    <span className="text-sm font-semibold text-slate-500">{SWARA_DEGREE_REFERENCE[i] || ""}</span>
                                    <ArrowRight size={12} className="text-slate-400" />
                                    <div className={`text-base font-semibold ${scaleMappingConfig.tone}`}>{formatSwara(activeScaleInfo.swaras[i] || "", scaleMappingConfig.tone)}</div>
                                    <span className="text-right text-sm font-semibold text-slate-600">{note}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-2 hidden overflow-x-auto sm:block">
                            <div className="min-w-[680px] space-y-2">
                                <div className="grid grid-cols-8 gap-2 rounded-md border border-slate-200 bg-slate-100/90 px-3 py-2 text-sm font-semibold text-slate-600">
                                    {activeScaleInfo.westernNotes.map((note, i) => <div key={`wn-${i}`} className="text-center">{note}</div>)}
                                </div>
                                <div className={`grid grid-cols-8 gap-2 rounded-md border px-3 py-2 text-base font-semibold ${scaleMappingConfig.border} ${scaleMappingConfig.bg} ${scaleMappingConfig.tone}`}>
                                    {activeScaleInfo.swaras.map((s, i) => <div key={`sw-${i}`} className="text-center">{formatSwara(s, scaleMappingConfig.tone)}</div>)}
                                </div>
                                <div className="grid grid-cols-8 gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600">
                                    {activeScaleInfo.swaras.map((s, i) => (
                                        <div key={`map-arrow-${i}`} className="text-center">
                                            <span>{SWARA_DEGREE_REFERENCE[i] || ""}</span>
                                            <span className="px-1 text-slate-400">→</span>
                                            {formatSwara(s, scaleMappingConfig.tone)}
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-8 gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-500">
                                    {SWARA_DEGREE_REFERENCE.map((d, i) => <div key={`dg-${i}`} className="text-center uppercase">{d}</div>)}
                                </div>
                            </div>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                            <span className="rounded-md border border-slate-300 bg-white px-2.5 py-1 font-mono">Target: {activeScaleInfo.targetScale}</span>
                            <span className="rounded-md border border-slate-300 bg-white px-2.5 py-1 font-mono">Komal: {activeScaleInfo.komalCount}</span>
                            <span className="rounded-md border border-slate-300 bg-white px-2.5 py-1 font-mono">Half-holes: {activeScaleInfo.halfHoleCount}</span>
                        </div>
                    </section>

                    <section className="tool-panel rounded-xl p-4">
                        <div className="flex flex-col items-center gap-3 text-center">
                            <div className="flex items-center gap-2">
                                <Gauge size={16} className="text-emerald-700" />
                                <p className="text-xs font-semibold text-slate-700 sm:text-sm">If this helped your riyaaz today, a small contribution keeps this tool alive for every learner.</p>
                            </div>
                            <a
                                href={STRIPE_DONATE_LINK}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 sm:text-sm"
                            >
                                <Heart size={14} />
                                Donate via Stripe
                            </a>
                        </div>
                    </section>

                    <section className="tool-panel rounded-xl p-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Feedback</p>
                        <p className="mt-1 text-xs text-slate-600 sm:text-sm">
                            If this is helpful for your practice, or if you face any issue, please let me know.
                        </p>
                        <form action="https://api.web3forms.com/submit" method="POST" className="mt-3 grid gap-2">
                            <input type="hidden" name="access_key" value="f08c85b7-a328-4759-82b0-6efb9ea57bd7" />
                            <input type="hidden" name="subject" value="Bansuri Scale Console Feedback" />
                            <input
                                type="text"
                                name="name"
                                required
                                placeholder="Your name"
                                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                            />
                            <input
                                type="email"
                                name="email"
                                required
                                placeholder="Your email"
                                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                            />
                            <textarea
                                name="message"
                                required
                                placeholder="Share feedback or report an issue"
                                rows={3}
                                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                            />
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 sm:text-sm"
                                >
                                    Send Feedback
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            </main>

            <footer className="px-4 pb-5 text-center text-xs text-slate-500 sm:px-6">
                made with love{" "}
                <a
                    href="https://kaagati.com"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-emerald-700 hover:text-emerald-800"
                >
                    kaagati.com
                </a>
            </footer>
        </>
    );
}
